package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.settings.phase
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.SegmentedControl
import co.maytes.standupbot.dashboard.components.widgets.SegmentedOption
import co.maytes.standupbot.dashboard.features.billing.rememberBillingStore
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.settings.BillingCopy
import co.maytes.standupbot.dashboard.features.settings.BillingStrip
import co.maytes.standupbot.dashboard.features.settings.BillingStripLoading
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.SettingsBars
import co.maytes.standupbot.dashboard.features.settings.SettingsEmpty
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsField
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsJump
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.billing.BillingIntervalDto
import co.maytes.standupbot.shared.dto.billing.BillingStatusDto
import co.maytes.standupbot.shared.dto.billing.PlanDto
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.launch

private const val UPGRADE_ANCHOR = "upgrade"
private const val SUBSCRIPTION_DESCRIPTION = "Upgrade, switch billing period or manage your card."

private val intervalOptions = listOf(
    SegmentedOption(BillingIntervalDto.ANNUAL, "Annual"),
    SegmentedOption(BillingIntervalDto.MONTHLY, "Monthly"),
)

@Composable
fun BillingSection() {
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val store = rememberBillingStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) store.refresh(workspaceId)
    }

    LaunchedEffect(Unit) {
        val search = window.location.search
        val matched = when {
            search.contains("status=success") -> {
                ui.showToast("Subscription activated.", ToastKind.Success)
                true
            }
            search.contains("status=cancel") -> {
                ui.showToast("Checkout canceled.", ToastKind.Info)
                true
            }
            else -> false
        }
        if (matched) {
            window.history.replaceState(null, "", window.location.pathname)
        }
    }

    val reload: () -> Unit = { scope.launch { workspaceId?.let { store.refresh(it) } } }
    val openCheckout: (PlanDto, BillingIntervalDto) -> Unit = { plan, interval ->
        scope.launch {
            when (val r = store.startCheckout(workspaceId ?: return@launch, plan, interval)) {
                is Result.Ok -> window.location.href = r.value.url
                is Result.Err -> ui.showToast(
                    "Couldn't start checkout: ${r.error.displayMessage}",
                    ToastKind.Error,
                )
            }
        }
    }
    val openPortal: () -> Unit = {
        scope.launch {
            when (val r = store.openPortal(workspaceId ?: return@launch)) {
                is Result.Ok -> window.location.href = r.value.url
                is Result.Err -> ui.showToast(
                    "Couldn't open billing portal: ${r.error.displayMessage}",
                    ToastKind.Error,
                )
            }
        }
    }

    val status = if (store.state == LoadState.Ready) store.status else null
    SettingsPage(
        group = SettingsGroup.Workspace,
        title = "Plan and billing",
        subtitle = status?.let { { BillingSubtitleLine(it) } },
        action = status?.takeIf { it.billingEnabled }?.let { { BillingAction(it, store.portalPending, openPortal) } },
    ) {
        StateSwap(state = store.state.phase()) {
            when (store.state) {
                LoadState.Idle, LoadState.Loading -> BillingLoading()
                is LoadState.Error -> BillingFailed(reload)
                LoadState.Ready -> if (status == null) {
                    BillingFailed(reload)
                } else {
                    BillingLoaded(
                        status = status,
                        checkoutPending = store.checkoutPending,
                        portalPending = store.portalPending,
                        onCheckout = openCheckout,
                        onPortal = openPortal,
                    )
                }
            }
        }
    }
}

@Composable
private fun BillingSubtitleLine(status: BillingStatusDto) {
    val copy = BillingCopy.subtitle(status)
    SettingsSubtitle {
        Strong(copy.lead)
        Plain(copy.rest)
    }
}

@Composable
private fun BillingAction(status: BillingStatusDto, portalPending: Boolean, onPortal: () -> Unit) {
    if (status.plan == PlanDto.FREE) {
        Button(
            label = "Upgrade to ${BillingCopy.planLabel(PlanDto.PRO)}",
            variant = ButtonVariant.Primary,
            onClick = { SettingsJump.to(UPGRADE_ANCHOR) },
        )
    } else {
        Button(
            label = if (portalPending) "Opening…" else "Open billing portal",
            variant = ButtonVariant.Primary,
            enabled = !portalPending,
            onClick = onPortal,
        )
    }
}

@Composable
private fun BillingLoading() {
    BillingStripLoading()
    SettingsSectionRow(anchor = UPGRADE_ANCHOR, title = "Subscription", description = SUBSCRIPTION_DESCRIPTION) {
        SettingsPanel { SettingsBars(rows = 3) }
    }
}

@Composable
private fun BillingFailed(onRetry: () -> Unit) {
    SettingsSectionRow(anchor = UPGRADE_ANCHOR, title = "Subscription", description = SUBSCRIPTION_DESCRIPTION) {
        SettingsPanel { SettingsError(what = "billing", onRetry = onRetry) }
    }
}

@Composable
private fun BillingLoaded(
    status: BillingStatusDto,
    checkoutPending: Boolean,
    portalPending: Boolean,
    onCheckout: (PlanDto, BillingIntervalDto) -> Unit,
    onPortal: () -> Unit,
) {
    BillingStrip(status)
    when {
        !status.billingEnabled -> BillingNotEnabledPanel()
        status.plan == PlanDto.FREE -> UpgradePanel(onCheckout = onCheckout, pending = checkoutPending)
        else -> ManagePanel(onPortal = onPortal, pending = portalPending)
    }
}

@Composable
private fun UpgradePanel(
    onCheckout: (PlanDto, BillingIntervalDto) -> Unit,
    pending: Boolean,
) {
    var interval by remember { mutableStateOf(BillingIntervalDto.ANNUAL) }

    SettingsSectionRow(
        anchor = UPGRADE_ANCHOR,
        title = "Upgrade",
        description = "Unlock unlimited standups and longer history.",
    ) {
        SettingsPanel {
            SettingsField(label = "Billing period", help = BillingCopy.UPGRADE_HINT) {
                SegmentedControl(
                    options = intervalOptions,
                    selected = interval,
                    onSelect = { interval = it },
                    ariaLabel = "Billing period",
                )
            }
            SettingsRowItem(
                title = BillingCopy.planLabel(PlanDto.PRO),
                description = BillingCopy.upgradeHighlights.joinToString(separator = " · "),
                titleTrailing = {
                    SpanText(
                        text = BillingCopy.priceLine(interval),
                        modifier = Modifier
                            .fontSize(Typography.label.size)
                            .fontWeight(400)
                            .color(Palette.textSecondary)
                            .styleModifier { property("font-variant-numeric", "tabular-nums") },
                    )
                },
                trailing = {
                    Button(
                        label = if (pending) "Opening Stripe…" else "Upgrade to ${BillingCopy.planLabel(PlanDto.PRO)}",
                        variant = ButtonVariant.Primary,
                        size = ButtonSize.Sm,
                        enabled = !pending,
                        onClick = { onCheckout(PlanDto.PRO, interval) },
                    )
                },
            )
            SpanText(
                text = BillingCopy.SALES_NOTE,
                modifier = Modifier.fontSize(Typography.bodySmall.size).color(Palette.textSecondary),
            )
        }
    }
}

@Composable
private fun BillingNotEnabledPanel() {
    SettingsSectionRow(
        anchor = UPGRADE_ANCHOR,
        title = "Billing is not enabled",
        description = BillingCopy.NOT_ENABLED_DESCRIPTION,
    ) {
        SettingsPanel { SettingsEmpty(text = BillingCopy.NOT_ENABLED_BODY) }
    }
}

@Composable
private fun ManagePanel(onPortal: () -> Unit, pending: Boolean) {
    SettingsSectionRow(
        anchor = UPGRADE_ANCHOR,
        title = "Manage subscription",
        description = "Cards, invoices and cancellation are handled in Stripe.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "Billing portal",
                first = true,
                description = BillingCopy.PORTAL_DESCRIPTION,
                trailing = {
                    Button(
                        label = if (pending) "Opening…" else "Open billing portal",
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        enabled = !pending,
                        onClick = onPortal,
                    )
                },
            )
        }
    }
}
